import Box from '@mui/material/Box';
import { Chip, Typography, useTheme } from '@mui/material';
import { useId } from 'react';

const AvatarWithProgress = ({
  src,
  alt,
  fallback,
  progress = 15,
  loading = false,
}: {
  src: string;
  alt: string;
  fallback: string;
  progress?: number;
  loading?: boolean;
}) => {
  const theme = useTheme();
  const uniqueId = useId();
  const maskId = `progressMask-${uniqueId}`;

  const svgWidth = 211;
  const svgHeight = 204;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;

  const startAngle = 135;
  const progressAngle = (progress / 100) * 360;
  const endAngle = startAngle + progressAngle;

  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  const radius = Math.max(svgWidth, svgHeight) * 1.5;

  const startX = centerX + radius * Math.cos(startRad);
  const startY = centerY + radius * Math.sin(startRad);
  const endX = centerX + radius * Math.cos(endRad);
  const endY = centerY + radius * Math.sin(endRad);

  const largeArcFlag = progress > 50 ? 1 : 0;

  const getPiePath = () => {
    if (progress === 0) {
      return '';
    }
    if (progress >= 100) {
      return `M 0 0 L ${svgWidth} 0 L ${svgWidth} ${svgHeight} L 0 ${svgHeight} Z`;
    }
    return `M ${centerX} ${centerY} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
  };

  const piePath = getPiePath();

  return (
    <Box
      sx={{
        position: 'relative',
        // More responsive sizing with additional breakpoints
        width: { xs: '120px', sm: '150px', md: '180px', lg: '228px' },
        height: { xs: '120px', sm: '150px', md: '180px', lg: '220px' },
        // Prevent shrinking in flex containers
        flexShrink: 0,
        minWidth: { xs: '120px', sm: '150px', md: '180px', lg: '228px' },
      }}
    >
      {/* Background shape */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: { xs: '8px', sm: '10px', md: '14px', lg: '17px' },
          width: { xs: '108px', sm: '136px', md: '166px', lg: '211px' },
          height: { xs: '104px', sm: '132px', md: '160px', lg: '204px' },
          bgcolor: '#F1F1FF',
          borderRadius: { xs: '54px', sm: '68px', md: '83px', lg: '83.97px' },
          zIndex: 1,
        }}
      />

      {/* Progress SVG - only show when not loading */}
      {!loading && (
        <Box
          component='svg'
          sx={{
            position: 'absolute',
            top: 0,
            left: { xs: '8px', sm: '10px', md: '14px', lg: '17px' },
            width: { xs: '108px', sm: '136px', md: '166px', lg: '211px' },
            height: { xs: '104px', sm: '132px', md: '160px', lg: '204px' },
            zIndex: 2,
          }}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          fill='none'
          preserveAspectRatio='xMidYMid meet'
        >
          <defs>
            <mask id={maskId}>
              <rect
                width={svgWidth}
                height={svgHeight}
                rx='83.97'
                fill='white'
              />
              <ellipse
                cx={svgWidth / 2}
                cy={svgHeight / 2}
                rx={99}
                ry={96}
                fill='black'
              />
            </mask>
          </defs>

          {progress > 0 && (
            <g mask={`url(#${maskId})`}>
              <path d={piePath} fill={theme.palette.primary.main} />
            </g>
          )}
        </Box>
      )}

      {/* Avatar image container */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '4px', sm: '5px', md: '5px', lg: '6px' },
          left: { xs: '12px', sm: '14px', md: '20px', lg: '24px' },
          width: { xs: '100px', sm: '128px', md: '155px', lg: '198px' },
          height: { xs: '96px', sm: '124px', md: '150px', lg: '192px' },
          borderRadius: { xs: '50px', sm: '64px', md: '77px', lg: '77.75px' },
          overflow: 'hidden',
          border: '2px solid white',
          zIndex: 3,
          // Fallback background with initial
          backgroundColor: theme.palette.grey[200],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component='img'
          src={src}
          alt={alt}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            display: 'block',
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none';
            // Show fallback
            const parent = e.currentTarget.parentElement;
            if (parent) {
              const fallbackEl = document.createElement('span');
              fallbackEl.textContent = fallback;
              fallbackEl.style.fontSize = '2rem';
              fallbackEl.style.fontWeight = '600';
              fallbackEl.style.color = theme.palette.text.secondary;
              parent.appendChild(fallbackEl);
            }
          }}
        />
      </Box>

      {/* Progress chip - only show when not loading */}
      {!loading && (
        <Chip
          label={
            <Typography
              sx={{
                fontFamily: 'Inter, Helvetica',
                fontSize: { xs: '11px', sm: '12px', md: '14px' },
                fontWeight: 400,
                letterSpacing: '0.17px',
                lineHeight: '143%',
                color: 'text.primary',
              }}
            >
              {progress}%
            </Typography>
          }
          sx={{
            position: 'absolute',
            bottom: { xs: '8px', sm: '12px', md: '20px' },
            left: { xs: '0px', sm: '2px', md: '5px', lg: '15px' },
            height: { xs: '36px', sm: '40px', md: '50px' },
            backgroundColor: 'background.paper',
            borderRadius: { xs: '14px', sm: '16px', md: '20px' },
            border: `2px solid ${theme.palette.primary.main}`,
            padding: { xs: '2px 4px', md: '4px 6px' },
            zIndex: 4,
            '& .MuiChip-label': {
              padding: 0,
            },
          }}
        />
      )}
    </Box>
  );
};

export default AvatarWithProgress;
